import { t } from "../../i18n";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  Building2,
  Cloud,
  Container,
  Download,
  Filter,
  Globe2,
  UserRound,
  LoaderCircle,
  Pencil,
  Plus,
  Power,
  RefreshCw,
  Trash2,
} from "lucide-react";
import type {
  CodexSubscriptionUsage,
  OpenCodeUsage,
  Provider,
} from "@lumen/shared";
import { api } from "../../api";
import { SettingsPage } from "../settings/ProfilePage";
import { ProviderEditor } from "../../components/integrations/ProviderEditor";
import { ProviderUsageDetails } from "../../components/integrations/ProviderUsageDetails";
import { ConfirmDangerDialog } from "../../components/shared/ConfirmDangerDialog";

export function ProvidersPage({
  canWrite,
  scope = "platform",
  organizationId,
  embedded = false,
  availableScopes,
  canWriteScopes,
}: {
  canWrite: boolean;
  scope?: "platform" | "personal" | "organization" | "all";
  organizationId?: string;
  embedded?: boolean;
  availableScopes?: Array<"platform" | "personal" | "organization">;
  canWriteScopes?: Array<"platform" | "personal" | "organization">;
}) {
  const [rows, setRows] = useState<Provider[]>([]);
  const [editing, setEditing] = useState<Provider | null | "new">(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState<Provider | null>(null);
  const [resetting, setResetting] = useState<Provider | null>(null);
  const [filter, setFilter] = useState<
    "all" | "platform" | "organization" | "personal"
  >("all");
  const [ollamaDrafts, setOllamaDrafts] = useState<Record<string, string>>({});
  const [ollamaDeleting, setOllamaDeleting] = useState<{
    provider: Provider;
    model: string;
  } | null>(null);
  const load = useCallback(
    () =>
      api<Provider[]>(
        `/providers?scope=${scope}${organizationId ? `&organizationId=${encodeURIComponent(organizationId)}` : ""}`,
      )
        .then(setRows)
        .catch((e) => setError(e.message)),
    [organizationId, scope],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const [subscriptionUsage, setSubscriptionUsage] = useState<
    Record<string, CodexSubscriptionUsage | OpenCodeUsage>
  >({});
  const [usageBusy, setUsageBusy] = useState<Record<string, boolean>>({});
  const [usageAttempted, setUsageAttempted] = useState<Record<string, boolean>>(
    {},
  );
  const loadUsage = useCallback(async (provider: Provider, force = false) => {
    setUsageBusy((current) => ({ ...current, [provider.id]: true }));
    try {
      const data = await api<CodexSubscriptionUsage | OpenCodeUsage>(
        `/providers/${provider.id}/usage${force ? "?refresh=1" : ""}`,
      );
      setSubscriptionUsage((current) => ({ ...current, [provider.id]: data }));
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Subscription usage could not be loaded",
      );
    } finally {
      setUsageBusy((current) => ({ ...current, [provider.id]: false }));
      setUsageAttempted((current) => ({ ...current, [provider.id]: true }));
    }
  }, []);
  const canWriteProvider = (provider: Provider) =>
    canWrite &&
    (scope !== "all" ||
      (provider.scope !== undefined &&
        canWriteScopes?.includes(provider.scope)));
  useEffect(() => {
    for (const provider of rows) {
      const manageable =
        canWrite &&
        (scope !== "all" ||
          (provider.scope !== undefined &&
            canWriteScopes?.includes(provider.scope)));
      if (
        manageable &&
        (provider.type === "codex-subscription" ||
          provider.type === "opencode-go") &&
        !usageAttempted[provider.id] &&
        !usageBusy[provider.id] &&
        subscriptionUsage[provider.id] === undefined
      ) {
        void loadUsage(provider);
      }
    }
  }, [
    canWrite,
    canWriteScopes,
    loadUsage,
    rows,
    scope,
    subscriptionUsage,
    usageAttempted,
    usageBusy,
  ]);
  const filteredRows = rows.filter(
    (provider) => filter === "all" || provider.scope === filter,
  );
  const sync = async (id: string) => {
    setBusy(id);
    setError("");
    try {
      await api(`/providers/${id}/sync`, { method: "POST" });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sync failed");
    } finally {
      setBusy("");
    }
  };
  const pullOllama = async (provider: Provider) => {
    const model = ollamaDrafts[provider.id]?.trim();
    if (!model) return;
    setBusy(provider.id);
    setError("");
    try {
      await api(`/providers/${provider.id}/ollama/pull`, {
        method: "POST",
        body: JSON.stringify({ model }),
      });
      setOllamaDrafts((current) => ({ ...current, [provider.id]: "" }));
      await api(`/providers/${provider.id}/sync`, { method: "POST" });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Ollama model pull failed");
    } finally {
      setBusy("");
    }
  };
  const deleteOllama = async () => {
    if (!ollamaDeleting) return;
    const { provider, model } = ollamaDeleting;
    setBusy(provider.id);
    setError("");
    try {
      await api(`/providers/${provider.id}/ollama/models`, {
        method: "DELETE",
        body: JSON.stringify({ model }),
      });
      await api(`/providers/${provider.id}/sync`, { method: "POST" });
      await load();
      setOllamaDeleting(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Ollama model delete failed");
    } finally {
      setBusy("");
    }
  };
  const toggle = async (provider: Provider) => {
    setBusy(provider.id);
    setError("");
    try {
      const updated = await api<Provider>(`/providers/${provider.id}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: !provider.enabled }),
      });
      setRows((current) =>
        current.map((row) =>
          row.id === provider.id ? { ...row, ...updated } : row,
        ),
      );
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Provider state could not be changed",
      );
    } finally {
      setBusy("");
    }
  };
  const resetModels = async () => {
    if (!resetting) return;
    setBusy(resetting.id);
    setError("");
    try {
      await api(`/providers/${resetting.id}/models/reset`, { method: "POST" });
      await load();
      setResetting(null);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Model configurations could not be reset",
      );
    } finally {
      setBusy("");
    }
  };
  const remove = async () => {
    if (!deleting) return;
    setBusy(deleting.id);
    setError("");
    try {
      await api(`/providers/${deleting.id}`, { method: "DELETE" });
      setRows((current) => current.filter((row) => row.id !== deleting.id));
      setDeleting(null);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Provider could not be deleted",
      );
    } finally {
      setBusy("");
    }
  };
  const title = scope === "personal" ? t("My providers") : t("Providers");
  const description =
    scope === "personal"
      ? "Connect a private API, Codex subscription, or Claude subscription. Its credentials and models are visible only to you."
      : "Connect APIs or a shared Codex subscription, then control visibility and imported models.";
  const action = canWrite ? (
    <div className="flex flex-wrap gap-2">
      <Link className="button" to="/settings/custom-providers">
        <Container size={16} /> {t("Custom providers")}
      </Link>
      <button className="button-primary" onClick={() => setEditing("new")}>
        <Plus size={17} /> {t("Add provider")}
      </button>
    </div>
  ) : undefined;
  const content = (
    <>
      {error && (
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}
      {scope === "all" && (
        <div className="mb-5 flex items-center gap-3">
          <Filter size={16} className="text-stone-500" />
          <label className="sr-only" htmlFor="provider-visibility-filter">
            {t("Filter by visibility")}
          </label>
          <select
            id="provider-visibility-filter"
            className="field max-w-xs"
            value={filter}
            onChange={(event) => setFilter(event.target.value as typeof filter)}
          >
            <option value="all">{t("All providers")}</option>
            <option value="platform">{t("Global")}</option>
            <option value="organization">{t("Organization")}</option>
            <option value="personal">{t("Personal")}</option>
          </select>
        </div>
      )}
      {editing && (
        <ProviderEditor
          // Remount the editor when the edit target changes so `form` (a
          // useState initializer) and ConfigFields' internal refs reset.
          // `editing` is either the string "new" or a Provider with an `id`.
          key={editing === "new" ? "new" : editing.id}
          provider={editing === "new" ? undefined : editing}
          scope={scope}
          organizationId={organizationId}
          availableScopes={availableScopes}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        {filteredRows.map((provider) => (
          <section
            key={provider.id}
            className={`panel p-5 ${provider.enabled ? "" : "opacity-75"}`}
          >
            <div className="flex items-start gap-4">
              <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-lumen-50 text-lumen-700 dark:bg-lumen-900/30 dark:text-lumen-300">
                {provider.scope === "platform" ? (
                  <Globe2 size={21} />
                ) : provider.scope === "organization" ? (
                  <Building2 size={21} />
                ) : (
                  <UserRound size={21} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold">{provider.name}</h3>
                <p className="mt-1 truncate text-xs text-stone-400">
                  {provider.type === "codex-subscription"
                    ? "ChatGPT subscription"
                    : provider.type === "claude-code-subscription"
                      ? "Claude Code subscription"
                      : provider.type === "custom"
                        ? (provider.customProviderDisplayName ??
                          t("Custom provider"))
                        : provider.baseUrl}
                </p>
                <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                  {provider.type === "custom" && (
                    <span className="rounded-full bg-violet-50 px-2 py-1 text-violet-700 dark:bg-violet-950/30 dark:text-violet-300">
                      {provider.customProviderDisplayName ??
                        t("Custom provider")}
                    </span>
                  )}
                  <span className="rounded-full bg-stone-100 px-2 py-1 dark:bg-white/5">
                    {t("model", { count: provider.models?.length ?? 0 })}
                  </span>
                  <span className="rounded-full bg-lumen-50 px-2 py-1 text-lumen-700 dark:bg-lumen-900/30 dark:text-lumen-300">
                    {provider.scope === "platform"
                      ? t("Global")
                      : provider.scope === "organization"
                        ? t("Organization")
                        : t("Personal")}
                  </span>
                  <span className="rounded-full bg-stone-100 px-2 py-1 dark:bg-white/5">
                    {t("{{policy}} policy", { policy: provider.modelPolicy })}
                  </span>
                  <span
                    className={`rounded-full px-2 py-1 ${provider.enabled ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300" : "bg-stone-100 text-stone-500 dark:bg-white/5"}`}
                  >
                    {provider.enabled ? "Enabled" : t("Disabled")}
                  </span>
                </div>
                {!provider.enabled && (
                  <p className="mt-3 text-xs text-stone-500">
                    {t(
                      "Disabled providers and their models are unavailable to users, but their configuration is retained.",
                    )}
                  </p>
                )}
              </div>
            </div>
            {canWriteProvider(provider) &&
              (provider.type === "codex-subscription" ||
                provider.type === "opencode-go") && (
                <div className="mt-5 rounded-xl border border-stone-200 p-4 dark:border-white/10">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <Activity size={15} /> {t("Subscription usage")}
                    </div>
                    <button
                      className="button border border-stone-200 px-2 py-1 dark:border-white/10"
                      title={t("Refresh")}
                      disabled={Boolean(usageBusy[provider.id])}
                      onClick={() => void loadUsage(provider, true)}
                    >
                      <RefreshCw
                        className={usageBusy[provider.id] ? "animate-spin" : ""}
                        size={13}
                      />
                    </button>
                  </div>
                  {subscriptionUsage[provider.id] ? (
                    <ProviderUsageDetails
                      provider={provider}
                      usage={subscriptionUsage[provider.id]}
                    />
                  ) : usageBusy[provider.id] ? (
                    <p className="mt-3 text-xs text-stone-400">
                      {t("Loading subscription usage…")}
                    </p>
                  ) : null}
                </div>
              )}
            {provider.type === "ollama" && canWriteProvider(provider) && (
              <div className="mt-5 rounded-xl border border-stone-200 p-4 dark:border-white/10">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Download size={15} /> {t("Ollama model library")}
                </div>
                <p className="mt-1 text-xs text-stone-500">
                  {t(
                    "Pull models into this Ollama server or remove models that are no longer needed.",
                  )}
                </p>
                <div className="mt-3 flex gap-2">
                  <input
                    className="field min-w-0 flex-1 font-mono text-xs"
                    placeholder={t("llama3.2:latest")}
                    value={ollamaDrafts[provider.id] ?? ""}
                    onChange={(event) =>
                      setOllamaDrafts((current) => ({
                        ...current,
                        [provider.id]: event.target.value,
                      }))
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") void pullOllama(provider);
                    }}
                  />
                  <button
                    className="button-primary"
                    disabled={
                      busy === provider.id || !ollamaDrafts[provider.id]?.trim()
                    }
                    onClick={() => void pullOllama(provider)}
                  >
                    {busy === provider.id ? (
                      <LoaderCircle className="animate-spin" size={15} />
                    ) : (
                      <Download size={15} />
                    )}
                    {t("Pull")}
                  </button>
                </div>
                {provider.models?.length ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {provider.models.map((model) => (
                      <span
                        key={model.id}
                        className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-1 font-mono text-[11px] dark:bg-white/5"
                      >
                        {model.remoteId}
                        <button
                          className="ml-1 text-red-600 dark:text-red-300"
                          title={t("Delete model")}
                          disabled={busy === provider.id}
                          onClick={() =>
                            setOllamaDeleting({
                              provider,
                              model: model.remoteId,
                            })
                          }
                        >
                          <Trash2 size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
            {canWriteProvider(provider) && (
              <div className="mt-5 flex flex-wrap justify-end gap-2">
                <button
                  className="button border border-stone-200 dark:border-white/10"
                  disabled={busy === provider.id}
                  onClick={() => void toggle(provider)}
                >
                  {busy === provider.id ? (
                    <LoaderCircle className="animate-spin" size={15} />
                  ) : (
                    <Power size={15} />
                  )}{" "}
                  {provider.enabled ? t("Disable") : t("Enable")}
                </button>
                <button
                  className="button border border-stone-200 dark:border-white/10"
                  onClick={() => setEditing(provider)}
                >
                  <Pencil size={15} /> {t("Edit")}
                </button>
                <button
                  className="button border border-stone-200 dark:border-white/10"
                  disabled={busy === provider.id || !provider.enabled}
                  onClick={() => void sync(provider.id)}
                >
                  <RefreshCw
                    className={busy === provider.id ? "animate-spin" : ""}
                    size={15}
                  />{" "}
                  {t("Sync")}
                </button>
                <button
                  className="button text-amber-700 dark:text-amber-300"
                  disabled={busy === provider.id || !provider.models?.length}
                  onClick={() => setResetting(provider)}
                >
                  <RefreshCw size={15} /> {t("Reset model configs")}
                </button>
                <button
                  className="button text-red-600 dark:text-red-300"
                  disabled={busy === provider.id}
                  onClick={() => setDeleting(provider)}
                >
                  <Trash2 size={15} /> {t("Delete")}
                </button>
              </div>
            )}
          </section>
        ))}
      </div>
      {filteredRows.length === 0 && !editing && (
        <div className="py-16 text-center text-stone-400">
          <Cloud className="mx-auto mb-3" size={32} />
          <p>{t("No providers connected yet.")}</p>
        </div>
      )}
      {resetting && (
        <ConfirmDangerDialog
          title={`Reset model configurations for ${resetting.name}?`}
          description={`This removes local names, descriptions, icons, tags, capability overrides, and tool settings from all ${resetting.models?.length ?? 0} models. All models are made available again.`}
          confirmLabel="Reset all model configs"
          busy={busy === resetting.id}
          onCancel={() => setResetting(null)}
          onConfirm={() => void resetModels()}
        />
      )}{" "}
      {deleting && (
        <ConfirmDangerDialog
          title={`Delete ${deleting.name}?`}
          description={`This permanently deletes the provider and all ${deleting.models?.length ?? 0} imported model configurations. Existing conversation history is retained, but these models will no longer be available.`}
          confirmLabel="Delete provider"
          busy={busy === deleting.id}
          onCancel={() => setDeleting(null)}
          onConfirm={() => void remove()}
        />
      )}
      {ollamaDeleting && (
        <ConfirmDangerDialog
          title={`Delete ${ollamaDeleting.model} from Ollama?`}
          description="This permanently removes the model and its local data from the configured Ollama server."
          confirmLabel="Delete Ollama model"
          busy={busy === ollamaDeleting.provider.id}
          onCancel={() => setOllamaDeleting(null)}
          onConfirm={() => void deleteOllama()}
        />
      )}
    </>
  );
  return embedded ? (
    <div>
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
          <p className="mt-2 text-sm text-stone-500">{description}</p>
        </div>
        {action}
      </header>
      {content}
    </div>
  ) : (
    <SettingsPage title={title} description={description} action={action}>
      {content}
    </SettingsPage>
  );
}
